import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import RingProgress from "@/components/RingProgress";

const today = () => new Date().toISOString().slice(0, 10);
const STRATEGIES = [
  "Split your steps: two 20-min walks a day.",
  "Walk during phone calls.",
  "Park at the far end of the lot.",
  "One after-dinner walk — every night.",
  "Weekend long walk: aim for 15K.",
];

export default function Move() {
  const [steps, setSteps] = useState(0);
  const [target, setTarget] = useState(10000);
  const [isLean, setIsLean] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: pw } = await api.get("/pathway/state");
      if (pw.active_pathway === "LEAN" && pw.block) {
        setIsLean(true);
        // Pull today's steps + target from the LEAN dashboard walk block
        const { data: dash } = await api.get("/lean/dashboard");
        setSteps(dash.walk.today_steps || 0);
        setTarget(dash.walk.current_step_target || 10000);
      } else {
        setIsLean(false);
        const { data } = await api.get("/track/logs");
        const t = data.find((l) => l.date === today());
        setSteps(t?.steps || 0);
        setTarget(10000);
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (isLean) {
        await api.post("/lean/log/steps", { date: today(), steps: Number(steps) });
      } else {
        await api.post("/track/log", { date: today(), steps: Number(steps) });
      }
      toast.success(`${Number(steps).toLocaleString()} steps logged for today.`);
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const pct = target ? Math.min(100, Math.round((Number(steps) / target) * 100)) : 0;

  return (
    <div className="space-y-8" data-testid="move-page">
      <div>
        <div className="label-eyebrow">Move · Walk Club</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">
          {isLean ? `${target.toLocaleString()} Steps A Day.` : "10K Steps A Day."}
        </h1>
        <p className="mt-2 text-muted-foreground text-sm">
          {isLean ? "Your LEAN step target — set once, held stable unless the Weekly Review says otherwise." : "Running is optional. Movement isn't."}
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-8 flex flex-col items-center" data-testid="move-log-card">
          <RingProgress value={Math.min(Number(steps), target)} max={target} label="Today" sublabel={`Target ${target.toLocaleString()}`} testId="steps-ring" />
          <div className="mt-4 text-center">
            <div className="wordmark text-3xl" data-testid="move-today-steps">
              {Number(steps).toLocaleString()} <span className="text-lg text-muted-foreground">/ {target.toLocaleString()}</span>
            </div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1" data-testid="move-today-pct">{pct}% complete today</div>
          </div>
          <div className="mt-6 flex gap-2 w-full">
            <input type="number" value={steps} onChange={(e) => setSteps(e.target.value)}
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Enter today's steps" data-testid="steps-input" />
            <button onClick={save} disabled={saving} className="btn-primary btn-sm" data-testid="steps-save">
              {saving ? "Saving…" : "Log"}
            </button>
          </div>
        </div>
        <div className="bg-foreground text-background rounded-lg p-8">
          <div className="label-eyebrow !text-background/70">Walking Strategies</div>
          <ul className="mt-4 space-y-3 text-sm">
            {STRATEGIES.map((s, i) => <li key={i} className="flex gap-3"><span className="text-primary">→</span>{s}</li>)}
          </ul>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="label-eyebrow">Walk Club Challenge</div>
        <p className="mt-3 text-sm text-muted-foreground">This month — 30 days, 6K minimum. Personal target, not competition.</p>
      </div>
    </div>
  );
}
